import { getAddressType } from "oc/utils/get-address-type";

export interface GetFormDataParams<
  V extends Record<string, any>,
  D extends Record<string, any>,
> {
  values: V;
  data: D;
  def: {
    type?: string;
  };
}

export interface FormDataItem {
  type: string;
  oldValue: any;
  newValue: any;
  newValueCode: string;
}

/**
 * Compares form `values` against existing `data` based on `def.type`,
 * and returns only the fields whose values have changed.
 */
export function getFormData<
  V extends Record<string, any>,
  D extends Record<string, any>,
>({ values, data, def }: GetFormDataParams<V, D>): { data: FormDataItem[] } {
  // Clone and remove non-form fields
  const valuesObject: Partial<V> = { ...values };
  delete (valuesObject as any).initialMessage;
  delete (valuesObject as any).validFrom;
  delete (valuesObject as any).addressSelector;

  const { isAddressType, addressType } = getAddressType({ def, data });

  const rawDataArray: FormDataItem[] = Object.keys(valuesObject).map((key) => {
    let oldValue = typeof data[key] === "object" && data[key] !== null
      ? data[key].name
      : data[key];

    const val = values[key];
    let newValue: any;
    let newValueCode = "";

    if (val !== null && typeof val === "object") {
      newValue = 'name' in val ? val.name : val;
      newValueCode = 'code' in val && typeof val.code === 'string' ? val.code : '';
    } else {
      newValue = val;
      newValueCode = typeof val === 'string' ? val : '';
    }

    // Special handling for address types
    if (isAddressType && addressType) {
      const addressRecord = data[addressType] as Record<string, any>;
      if (key === "streetType") {
        oldValue = addressRecord[key]?.name;
        newValueCode = addressRecord[key]?.code ?? "";
      } else {
        oldValue = addressRecord[key];
      }
    }

    // Phones: values[key] holds string or formatted number
    if (def.type === "phones") {
      const phoneArray = data.phones as Array<{ type: string; number: string }>;
      const typeKey = key.slice(0, -5); // remove "Phone"
      const found = phoneArray.find((item) => item.type === typeKey);
      oldValue = found?.number ?? "";
      if (typeof newValue === "string" && !newValue.startsWith("+36")) {
        newValue = `+36 ${newValue.trim()}`;
      }
      if (newValue === "+36") {
        newValue = "";
      }
    }

    // Emails: data.emails is array of { type, address }
    if (def.type === "emails") {
      const emailArray = data.emails as Array<{ type: string; address: string }>;
      const typeKey = key.slice(0, -5); // remove "Email"
      const found = emailArray.find((item) => item.type === typeKey);
      oldValue = found?.address ?? "";
    }

    const item: FormDataItem = {
      type: key,
      oldValue,
      newValue,
      newValueCode,
    };

    // For date fields, preserve the raw value (e.g. Date object or string)
    if (key === "dateOfBirth") {
      item.newValue = values[key];
    }

    return item;
  });

  // Only keep fields where the value actually changed
  const dataArray = rawDataArray.filter(
    (item) => item.oldValue !== item.newValue
  );

  return { data: dataArray };
}

export default getFormData;
